/**
 * THE REQUEST BUDGET — a semaphore, and the reason it exists is a measured
 * production incident rather than tidiness.
 *
 * Production runs `DB_POOL_MAX` unset, i.e. **5**
 * (lobbero_be/src/middleware/scope.js records the deadlock that established the
 * number). Every route this screen calls opens an RLS transaction, and a
 * transaction is one pooled connection held for the life of the request. So a
 * screen that fires nine panels as nine requests is nine connections, and three
 * managers opening it at once is exactly the concurrency that stalled the whole
 * application.
 *
 * The analytics screen solved that by hand: one batch instead of nine requests,
 * then the four non-count panels in "two waves of two", choreographed with
 * effects and a comment explaining the arithmetic. It works and it is fragile —
 * the shape of the waves is implied by the shape of the effects, so adding a
 * panel means re-deriving it.
 *
 * This is the same arithmetic as a rule instead. Four in flight, at most two of
 * them analytics routes. Adding a request cannot break it, because the gate is
 * not something a caller opts into.
 *
 * ── A QUEUED REQUEST WHOSE QUESTION CHANGED IS NEVER SENT ──
 *
 * A reader dragging a range chip generates a request per stop. Without this the
 * queue drains them all, in order, long after the answer stopped being wanted —
 * and each one costs a connection. A queued task whose slice key has moved on is
 * dropped IN THE QUEUE, so only the last question is ever asked.
 */

export interface BudgetOptions {
  maxConcurrent?: number;
  maxAnalyticsConcurrent?: number;
}

export interface Task<T> {
  /** Ordering. Lower runs first: meta, then the figures above the fold. */
  priority: number;
  /** `/analytics/*` routes count against the tighter of the two limits. */
  analytics: boolean;
  /** Asked immediately before starting. False means the question changed while
   *  this waited, and the request is dropped rather than sent. */
  stillWanted: () => boolean;
  run: () => Promise<T>;
}

interface Queued {
  task: Task<unknown>;
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}

/** The sentinel a dropped task rejects with, so a caller can tell "we did not
 *  ask any more" from "the server refused". */
export class RequestSuperseded extends Error {
  constructor() {
    super('superseded');
    this.name = 'RequestSuperseded';
  }
}

export const isSuperseded = (error: unknown): boolean =>
  error instanceof RequestSuperseded ||
  (typeof error === 'object' &&
    error !== null &&
    (error as { name?: string }).name === 'CanceledError');

export interface Budget {
  submit: <T>(task: Task<T>) => Promise<T>;
  /** For the tests and for a debug readout. */
  inFlight: () => { total: number; analytics: number; queued: number };
}

export const createBudget = (options: BudgetOptions = {}): Budget => {
  const maxConcurrent = options.maxConcurrent ?? 4;
  const maxAnalytics = options.maxAnalyticsConcurrent ?? 2;

  let running = 0;
  let runningAnalytics = 0;
  const queue: Queued[] = [];

  const canStart = (task: Task<unknown>): boolean =>
    running < maxConcurrent &&
    (!task.analytics || runningAnalytics < maxAnalytics);

  const pump = () => {
    // Highest priority first. A stable sort is not required — two tasks of equal
    // priority are interchangeable by definition.
    queue.sort((a, b) => a.task.priority - b.task.priority);

    for (let i = 0; i < queue.length; i++) {
      const entry = queue[i];

      // Dropped in the queue rather than sent. See the header.
      if (!entry.task.stillWanted()) {
        queue.splice(i, 1);
        i--;
        entry.reject(new RequestSuperseded());
        continue;
      }

      if (!canStart(entry.task)) continue;

      queue.splice(i, 1);
      i--;
      running++;
      if (entry.task.analytics) runningAnalytics++;

      /* ═══ THE SLOT IS RELEASED WHATEVER `run()` DOES ═══
       *
       * `run()` is a caller's function, and two of the ways it can misbehave are
       * indistinguishable from a hang:
       *
       *   · it THROWS SYNCHRONOUSLY (a bad argument, a null deref before the
       *     first await),
       *   · it returns something that is not a promise at all.
       *
       * Calling `.then()` on either of those throws INSIDE this loop, after the
       * counter has been incremented — so the slot is never given back. With two
       * analytics slots and a screen that fires seven analytics requests, two such
       * failures stall the entire screen permanently: nothing errors, nothing
       * loads, every panel sits on its skeleton forever. That is a far worse
       * symptom than the bug that caused it, and it was found exactly this way.
       *
       * `Promise.resolve().then(() => run())` converts both into a rejection, so
       * the `.finally` below always runs and the caller sees a real error.
       */
      Promise.resolve()
        .then(() => entry.task.run())
        .then(entry.resolve, entry.reject)
        .finally(() => {
          running--;
          if (entry.task.analytics) runningAnalytics--;
          // A microtask, so a synchronous resolve cannot recurse into pump()
          // from inside its own finally and blow the stack on a warm cache.
          Promise.resolve().then(pump);
        });
    }
  };

  return {
    submit: <T,>(task: Task<T>): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        queue.push({
          task: task as Task<unknown>,
          resolve: resolve as (value: unknown) => void,
          reject,
        });
        pump();
      }),
    inFlight: () => ({
      total: running,
      analytics: runningAnalytics,
      queued: queue.length,
    }),
  };
};

/**
 * The screen's own budget. ONE instance, because the limit is a property of the
 * database's connection pool and not of a component — two budgets of four is
 * eight, which is the thing this exists to prevent.
 */
export const budget = createBudget();
